import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Button — the one button for Student, Manager and Admin (ADR-0088 D2).
 *
 * It wraps no headless primitive: a native `<button>` is already accessible,
 * so there is no behaviour to borrow (ADR-0088 D3). It replaces four copies:
 * Admin's shadcn button, Web's shadcn button on Base UI, Web's hand-written
 * auth-page button, and the `sc-btn` CSS classes in Student and Manager.
 *
 * **One set of roles, two shapes.** The variants name a role, not a color, so
 * a token correction happens in one place. The sizes carry the shape:
 *
 * - `xs` to `field` (28–40 px) and the icon squares are the dense operator
 *   rhythm Admin is built on: the control corner for their height (4 px up to
 *   32 px, 6 px at 36–40 px), medium weight.
 * - `lg` (44 px) and `xl` (52 px) are the customer shape Student and Manager
 *   use: the large control corner (8 px), semibold, the learner font with its Japanese
 *   fallbacks, and a 44 px touch target from the smaller of the two.
 *
 * **The disabled look comes from the attribute.** `disabled:` variants derive
 * it from the real `disabled` attribute, so a button can no longer be
 * disabled and look enabled, or the reverse.
 *
 * `data-slot="button"`, `data-variant` and `data-size` are the hooks an app
 * stylesheet uses to adjust a button in a specific layout (a call dock, a
 * hero card) without restyling it.
 */
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from './cn.js';
// The control corner grows with the control: 4 px up to 32 px tall, 6 px at
// 36–40 px, 8 px for the 44–52 px customer sizes (design tokens §4).
const dense = 'text-body font-medium';
const customer = 'border py-0 font-learner font-semibold tracking-tight rounded-token-input-lg';
export const buttonVariants = cva(
// On a touch screen every button is at least 44 x 44 px (design tokens §7).
'inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer pointer-coarse:min-h-11 pointer-coarse:min-w-11 ' +
    '[transition:background-color_var(--motion-fast),border-color_var(--motion-fast),color_var(--motion-fast),outline-color_var(--motion-fast),transform_var(--motion-fast)] ' +
    'motion-reduce:transition-none active:[transform:translateY(0.5px)] ' +
    // Focus: the ring 2 px out from a filled button (primary, danger), in the
    // fill's colour, following it on hover; laid over the border of the
    // others, so it reads as a thicker border. The outline holds the ring's
    // colour while unfocused (it is not drawn), so only the hover change
    // animates, never a flash from the text colour as focus arrives.
    '[outline-color:var(--focus-ring-color,var(--color-focus))] focus-visible:focus-ring ' +
    'disabled:cursor-not-allowed disabled:opacity-50 data-[pending=true]:disabled:opacity-100', {
    variants: {
        variant: {
            /**
             * The one action a form or screen exists for. Disabled, the fill fades
             * to half and the label turns ochre, in place of the shared dimming,
             * which turned the ink label grey on the yellow. A pending write retains
             * its fill and ink while activation stays disabled.
             */
            primary: 'border-transparent bg-accent text-on-accent hover:bg-accent-hover hover:[--focus-ring-color:var(--color-accent-hover)] ' +
                'disabled:bg-accent/50 disabled:text-accent-deep disabled:opacity-100 ' +
                'data-[pending=true]:disabled:bg-accent data-[pending=true]:disabled:text-on-accent',
            /**
             * Everything that is not the primary action: cancel, retry, paginate,
             * a second submit. Transparent, so it sits on a card or on the app
             * background alike; the hover tint works on both.
             */
            secondary: 'border border-border-default bg-transparent text-text-primary ' +
                'hover:border-border-control hover:bg-control-hover [--focus-ring-offset:-2px]',
            /** A filled media action, visually distinct from outlined choices. */
            contrast: 'border-transparent bg-contrast text-on-contrast hover:bg-contrast-hover [--focus-ring-offset:2px]',
            /** A quiet text action inside a filter, toolbar, banner or compact card. */
            ghost: 'border-transparent bg-transparent text-text-secondary hover:bg-control-hover hover:text-text-primary [--focus-ring-offset:-2px]',
            /** Confirming a destructive action, inside its dialog: white on the danger fill (4.69:1). */
            danger: 'border-transparent bg-danger text-on-danger hover:bg-danger-hover [--focus-ring-color:var(--color-danger)] hover:[--focus-ring-color:var(--color-danger-hover)]',
            /** Opening a reversible but consequential change. Outlined, because it is not the act, in the warning colour. */
            warningOpen: 'border border-warning text-text-primary hover:bg-warning/10 [--focus-ring-color:var(--color-warning)] [--focus-ring-offset:-2px]',
            /**
             * OPENING a destructive flow, which `danger` then confirms. Outlined,
             * because it is not the act, in the same red as the danger fill.
             */
            dangerOpen: 'border border-danger text-text-primary hover:bg-danger/10 [--focus-ring-color:var(--color-danger)] [--focus-ring-offset:-2px]',
        },
        size: {
            /**
             * A row utility inside a dense or embedded table, where even `sm`
             * reads taller than the row it serves.
             */
            xs: `h-7 px-2 rounded-token-input-sm ${dense} text-label`,
            sm: `h-8 px-2.5 rounded-token-input-sm ${dense}`,
            default: `h-9 px-3 rounded-token-input ${dense}`,
            /** Level with a 40 px form field beside it. */
            field: `h-10 px-3 rounded-token-input ${dense}`,
            /** A compact icon-only square in a dense header. Needs an `aria-label`. */
            iconSm: `h-7 w-7 px-0 rounded-token-input-sm ${dense}`,
            /** An icon-only square. Needs an `aria-label`. */
            icon: `h-8 w-8 px-0 rounded-token-input-sm ${dense}`,
            /** An icon-only square standing in for a whole surface (a collapsed rail). */
            iconLg: `h-10 w-10 px-0 rounded-token-input ${dense}`,
            /** The customer button. */
            lg: `h-11 px-4.5 text-body ${customer}`,
            /** The customer button for a screen's single main action. */
            xl: `h-13 px-6.5 text-body ${customer}`,
        },
        /** Fill the container's width, for stacked actions on a narrow card. */
        block: { true: 'w-full', false: '' },
    },
    compoundVariants: [{ variant: 'contrast', className: 'rounded-token-pill' }],
    defaultVariants: { variant: 'secondary', size: 'default', block: false },
});
export const Button = forwardRef(({ className, variant = 'secondary', size = 'default', block, type = 'button', pending = false, disabled, ...props }, ref) => (
// `type="button"` by default: an unspecified <button> inside a <form>
// submits it. A submit button says `type="submit"` on purpose.
_jsx("button", { ref: ref, type: type, "data-slot": "button", "data-variant": variant, "data-size": size, "data-pending": pending || undefined, "aria-busy": pending || undefined, disabled: disabled || pending, className: cn(buttonVariants({ variant, size, block }), className), ...props })));
Button.displayName = 'Button';
//# sourceMappingURL=button.js.map